import React from "react";
import { useTranslation } from "react-i18next";
import {
  FaFacebookSquare,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

// Mock wizard logo component since we can't import the actual image
const WizardLogo = () => (
  <div className="flex items-center space-x-3">
    <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gray-800/80 backdrop-blur-sm border border-gray-700 group-hover:border-purple-500/50 transition-colors duration-300 overflow-hidden">
      <img
        src={require("src/assets/wizard-logo.jpg")}
        alt="Wizard Logo"
        className="w-8 h-8 object-contain transition-transform duration-500 group-hover:rotate-6"
      />
    </div>
    <span className="text-2xl font-bold bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent tracking-tight">
      Wizard
    </span>
  </div>
);

interface SocialIconProps {
  icon: React.ElementType;
  url: string;
  name: string;
}

const SocialIcon: React.FC<SocialIconProps> = ({ icon: Icon, url, name }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="group relative p-3 rounded-full bg-gray-800/50 border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 hover:bg-gradient-to-r hover:from-purple-600/20 hover:to-indigo-600/20 hover:scale-110"
    aria-label={name}
  >
    <Icon
      className="text-gray-400 group-hover:text-purple-400 transition-colors duration-300"
      size={20}
    />
    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
      {name}
    </div>
  </a>
);

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const socialItems = [
    // {
    //   icon: FaFacebookSquare,
    //   url: "https://facebook.com/wizardplatform",
    //   name: "Facebook",
    // },
    // {
    //   icon: FaInstagram,
    //   url: "",
    //   name: "Instagram",
    // },
    // {
    //   icon: FaXTwitter,
    //   url: "",
    //   name: "x",
    // },
    // {
    //   icon: FaLinkedinIn,
    //   url: "https://linkedin.com/company/wizardplatform",
    //   name: "LinkedIn",
    // },
    {
      icon: FaYoutube,
      url: "https://www.youtube.com/@WizardPlatform",
      name: "YouTube",
    },
  ];

  const footerSections = [
    {
      title: t("footer.features"),
      items: [
        { text: t("header.app"), link: "/app" },
        { text: t("header.product"), link: "/product" },
        { text: t("footer.home"), link: "/" },
      ],
    },
    {
      title: t("footer.support"),
      items: [
        { text: t("header.pricing"), link: "/pricing" },
        // {
        //   text: t("footer.guides"),
        //   link: "https://wizardfootball.com/guides/Wizard%20Football%20-%20User%20guide%20V.5.3%20-%20Google%20Docs.pdf",
        // },
        {
          text: t("footer.documentation"),
          link: "https://docs.wizardfootball.com/",
        },
        // { text: t("footer.contact"), link: "/contact" },
      ],
    },
    {
      title: t("header.company"),
      items: [
        { text: t("footer.about_us"), link: "/company" },
        // { text: "Careers", link: "/" },
        // { text: "Blog", link: "/" },
        // { text: "Press", link: "/" },
      ],
    },
    {
      title: "Legal",
      items: [
        { text: t("footer.privacy_policy"), link: "/legal#privacy" },
        { text: t("footer.terms_of_service"), link: "/legal#terms" },
        // { text: t("footer.cookie_policy"), link: "/legal" },
        // { text: "Legal", link: "/legal" },
      ],
    },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Top section with logo and social icons */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center py-12 border-b border-gray-800/50">
          <div className="mb-8 lg:mb-0">
            <WizardLogo />
          </div>

          {/* Social Media Icons */}
          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm font-medium mr-4 hidden sm:block">
              {t("footer.follow_us")}
            </span>
            {socialItems.map((item, index) => (
              <SocialIcon
                key={index}
                icon={item.icon}
                url={item.url}
                name={item.name}
              />
            ))}
          </div>
        </div>

        {/* Main footer content */}
        <div className="py-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Company description */}
          <div className="lg:col-span-2">
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {t("footer.description")}
            </p>

            {/* Newsletter signup */}
            {/* <div className="space-y-4">
              <h4 className="text-white font-semibold text-lg">
                {t("footer.stay_updated")}
              </h4>
              <div className="flex max-w-md">
                <input
                  type="email"
                  placeholder={t("footer.enter_your_email")}
                  className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-700/50 rounded-l-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors duration-300"
                />
                <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-r-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105">
                  {t("footer.subscribe")}
                </button>
              </div>
            </div> */}
          </div>

          {/* Footer sections */}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-8">
            {footerSections.map((section, index) => (
              <div key={index} className="space-y-4">
                <h3 className="text-white font-semibold text-lg">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      <a
                        href={item.link}
                        target="_blank"
                        className="text-gray-400 hover:text-purple-400 transition-colors duration-300 text-sm font-medium hover:underline"
                      >
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="py-8 border-t border-gray-800/50 ">
          <div className="flex flex-col md:flex-row justify-end  items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2026 Wizard Platform. {t("footer.all_rights_reserved")}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;